import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, ExternalLink, FileText, Globe } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const colorMap = {
    drafted: "text-purple-400 border-purple-400",
    published: "text-emerald-400 border-emerald-400",
    failed: "text-red-400 border-red-400",
  };
  
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-mono uppercase tracking-wide border rounded-full ${colorMap[status] || 'text-zinc-400 border-zinc-400'}`}>
      {status}
    </span>
  );
};

export default function Content() {
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [outputFiles, setOutputFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pagesRes, statsRes, filesRes] = await Promise.all([
        axios.get(`${API}/pages`),
        axios.get(`${API}/pages/stats`),
        axios.get(`${API}/output/files`)
      ]);
      setPages(pagesRes.data);
      setStats(statsRes.data);
      setOutputFiles(filesRes.data);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const viewPageContent = async (file) => {
    try {
      const contentPath = `${file.path}/content.md`;
      const metaPath = `${file.path}/meta.json`;
      
      const [contentRes, metaRes] = await Promise.all([
        axios.get(`${API}/output/file`, { params: { path: contentPath } }),
        axios.get(`${API}/output/file`, { params: { path: metaPath } })
      ]);
      
      setPageContent({
        content: contentRes.data.content,
        meta: metaRes.data
      });
      setSelectedPage(file);
    } catch (error) {
      console.error("Error loading page:", error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="content-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono font-medium text-white">Content</h1>
        <p className="text-sm text-zinc-500">Generated pages and output files</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Pages</div>
            <div className="font-mono text-2xl text-white">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Published</div>
            <div className="font-mono text-2xl text-emerald-400">{stats?.by_status?.published || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Words</div>
            <div className="font-mono text-2xl text-white">{(stats?.total_words || 0).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Avg Words</div>
            <div className="font-mono text-2xl text-white">{stats?.avg_words || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-4">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Output Files</div>
            <div className="font-mono text-2xl text-white">{outputFiles.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Output Files */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Published Files
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-500 text-xs uppercase">Title</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Domain</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Type</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Generated</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outputFiles.map((file, idx) => (
                  <TableRow key={idx} className="border-zinc-800">
                    <TableCell className="text-white font-medium">{file.title || file.slug}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-500">{file.domain}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{file.page_type}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-500">
                      {file.generated_at ? new Date(file.generated_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewPageContent(file)}
                        className="text-zinc-500 hover:text-white"
                        data-testid={`view-${file.slug}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {outputFiles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                      No files published yet. Run the generator to create content.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pages Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300 flex items-center gap-2">
            <Globe className="h-4 w-4" />
            All Generated Pages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-500 text-xs uppercase">Title</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Type</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Domain</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Status</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">QA</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Words</TableHead>
                  <TableHead className="text-zinc-500 text-xs uppercase">Links</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pages.map((page) => (
                  <TableRow key={page.id} className="border-zinc-800">
                    <TableCell className="text-white font-medium max-w-xs truncate">{page.title}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{page.page_type}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-500">{page.target_domain}</TableCell>
                    <TableCell><StatusBadge status={page.status} /></TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono ${page.qa_status === 'passed' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {page.qa_status || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400">{page.word_count || 0}</TableCell>
                    <TableCell className="font-mono text-zinc-400">{page.internal_links_count || 0}</TableCell>
                  </TableRow>
                ))}
                {pages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                      No pages generated yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Page Content Dialog */}
      <Dialog open={!!selectedPage} onOpenChange={() => { setSelectedPage(null); setPageContent(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">{selectedPage?.title || selectedPage?.slug}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] space-y-4">
            {pageContent?.meta && (
              <div className="bg-zinc-800 p-4 rounded-md">
                <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Meta</h3>
                <pre className="text-xs text-zinc-300 font-mono overflow-x-auto">
                  {JSON.stringify(pageContent.meta, null, 2)}
                </pre>
              </div>
            )}
            {pageContent?.content && (
              <div className="bg-black p-4 rounded-md">
                <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Content</h3>
                <pre className="text-sm text-zinc-300 font-mono whitespace-pre-wrap">
                  {pageContent.content}
                </pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
